// Composer — prompt + chips for style/aspect/quality/count + an
// "Advanced" expander for negative + seed + reference image + Redux +
// Canny + ControlNet model.
//
// Settings (style/quality/aspect) auto-persist via imageGen.updateSettings
// so the user lands on their preferred defaults each session. Reference
// image + ref/Canny knobs are per-generation (not persisted).

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import {
  CONTROLNET_MODELS,
  type ControlNetModel,
  type ImageGenOptions,
  type ImageGenPrefs,
  type ImageGenGenerateArgs,
} from "../../lib/rpc";

interface ComposerProps {
  options: ImageGenOptions | null;
  prefs: ImageGenPrefs | null;
  setPrefs: (next: ImageGenPrefs) => Promise<void>;
  generating: boolean;
  onSubmit: (args: ImageGenGenerateArgs) => void;
  notify: (msg: string) => void;
}

const COUNT_OPTIONS = [1, 4, 6, 9];
const MAX_REF_BYTES = 16 * 1024 * 1024; // matches server cap

export function Composer({ options, prefs, setPrefs, generating, onSubmit, notify }: ComposerProps) {
  const [prompt, setPrompt] = useState("");
  const [count, setCount] = useState(1);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [negative, setNegative] = useState("");
  const [seedStr, setSeedStr] = useState("");

  // Reference image — data URL we send to the server; preview reuses
  // the same string. Cleared via X.
  const [refDataUrl, setRefDataUrl] = useState<string | null>(null);
  const [refStrength, setRefStrength] = useState(1.0);
  const [useRedux, setUseRedux] = useState(true);
  const [useCannyLock, setUseCannyLock] = useState(false);
  const [cannyStrength, setCannyStrength] = useState(0.7);
  const [cnModel, setCnModel] = useState<ControlNetModel>(CONTROLNET_MODELS[0].id);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [style, setStyle] = useState<string>("");
  const [quality, setQuality] = useState<string>("");
  const [aspect, setAspect] = useState<string>("");
  useEffect(() => {
    if (!prefs) return;
    if (!style)   setStyle(prefs.style);
    if (!quality) setQuality(prefs.quality);
    if (!aspect)  setAspect(prefs.aspect);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefs]);

  // Auto-expand the Advanced panel once a ref image is added so the
  // user sees the strength/Redux/Canny controls without hunting.
  useEffect(() => {
    if (refDataUrl) setAdvancedOpen(true);
  }, [refDataUrl]);

  const submit = () => {
    const text = prompt.trim();
    if (!text || generating) return;
    const seed = seedStr.trim() ? Number(seedStr.trim()) : undefined;
    const refFields = refDataUrl ? {
      referenceImageDataUrl: refDataUrl,
      refStrength,
      useRedux,
      useCannyLock,
      cannyStrength: useCannyLock ? cannyStrength : undefined,
      controlnetModel: useCannyLock ? cnModel : undefined,
    } : {};
    onSubmit({
      prompt: text,
      style, quality, aspect,
      candidates: count,
      seed: Number.isFinite(seed) ? seed : undefined,
      negative_extra: negative.trim() || undefined,
      ...refFields,
    });
  };

  const onChangeChip = (kind: "style" | "quality" | "aspect", value: string) => {
    if (kind === "style") setStyle(value);
    if (kind === "quality") setQuality(value);
    if (kind === "aspect") setAspect(value);
    const next: ImageGenPrefs = {
      style:   kind === "style"   ? value : style   || (prefs?.style   ?? ""),
      quality: kind === "quality" ? value : quality || (prefs?.quality ?? ""),
      aspect:  kind === "aspect"  ? value : aspect  || (prefs?.aspect  ?? ""),
    };
    setPrefs(next);
  };

  // Common file → data URL with allow-list + size cap. Returns null on
  // failure (notify already fired). Used by both the picker and drop.
  const ingestFile = async (file: File): Promise<string | null> => {
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      notify("Reference must be PNG, JPEG, or WebP.");
      return null;
    }
    if (file.size > MAX_REF_BYTES) {
      notify(`Reference image is over 16 MB.`);
      return null;
    }
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = () => { notify("Couldn't read the image."); resolve(null); };
      reader.readAsDataURL(file);
    });
  };

  const onPickFile = async (e: Event) => {
    const file = (e.currentTarget as HTMLInputElement).files?.[0];
    if (!file) return;
    const url = await ingestFile(file);
    if (url) setRefDataUrl(url);
    // Reset the input so the same file re-pickable triggers change.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onDrop = async (e: DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const url = await ingestFile(file);
    if (url) setRefDataUrl(url);
  };

  const renderChips = (
    kind: "style" | "quality" | "aspect",
    enums: Record<string, { label: string; description: string | null }>,
    active: string,
  ) => (
    <div className="studio-chip-row">
      {Object.entries(enums).map(([key, { label, description }]) => (
        <button
          key={key}
          type="button"
          className="studio-chip"
          data-active={active === key}
          onClick={() => onChangeChip(kind, key)}
          title={description || undefined}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="studio-composer">
      <div className="studio-prompt-wrap">
        <Icon name="sparkle" size={18} />
        <textarea
          className="studio-prompt"
          placeholder="Describe what you'd like to see…"
          rows={2}
          value={prompt}
          onInput={(e) => setPrompt((e.currentTarget as HTMLTextAreaElement).value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          className="btn btn-primary studio-submit"
          onClick={submit}
          disabled={!prompt.trim() || generating}
          type="button"
        >
          <Icon name="send" size={14} />
          {generating ? "Generating…" : "Generate"}
        </button>
      </div>

      {options && (
        <>
          <ComposerRow label="Style">
            {renderChips("style", options.style, style)}
          </ComposerRow>
          <ComposerRow label="Aspect">
            {renderChips("aspect", options.aspect, aspect)}
          </ComposerRow>
          <ComposerRow label="Quality">
            {renderChips("quality", options.quality, quality)}
          </ComposerRow>
        </>
      )}

      <ComposerRow label="Count">
        <div className="studio-chip-row">
          {COUNT_OPTIONS.map((n) => (
            <button
              key={n}
              type="button"
              className="studio-chip"
              data-active={count === n}
              onClick={() => setCount(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </ComposerRow>

      <button
        className="studio-advanced-toggle"
        onClick={() => setAdvancedOpen((v) => !v)}
        type="button"
      >
        <Icon name={advancedOpen ? "x" : "plus"} size={12} /> Advanced
      </button>
      {advancedOpen && (
        <div className="studio-advanced">
          <ComposerRow label="Negative (extra)">
            <input
              className="input"
              placeholder="Things you'd rather not see…"
              value={negative}
              onInput={(e) => setNegative((e.currentTarget as HTMLInputElement).value)}
            />
          </ComposerRow>
          <ComposerRow label="Seed (optional)">
            <input
              className="input"
              type="number"
              placeholder="Random when blank"
              value={seedStr}
              onInput={(e) => setSeedStr((e.currentTarget as HTMLInputElement).value)}
            />
          </ComposerRow>

          {/* Reference image + Redux/Canny/ControlNet model */}
          <ComposerRow label="Reference image">
            {refDataUrl ? (
              <div className="studio-ref">
                <img className="studio-ref-thumb" src={refDataUrl} alt="reference" />
                <div className="studio-ref-body">
                  <div className="studio-ref-meta">
                    Used as composition reference. Toggle Redux/Canny below
                    to control how strictly the output adheres.
                  </div>
                  <div className="studio-ref-row">
                    <label className="studio-ref-slider-label">Strength</label>
                    <input
                      type="range"
                      min={0} max={2} step={0.05}
                      value={refStrength}
                      onInput={(e) => setRefStrength(Number((e.currentTarget as HTMLInputElement).value))}
                    />
                    <span className="studio-ref-slider-val">{refStrength.toFixed(2)}</span>
                  </div>
                  <label className="studio-toggle">
                    <input
                      type="checkbox"
                      checked={useRedux}
                      onChange={(e) => setUseRedux((e.currentTarget as HTMLInputElement).checked)}
                    />
                    Use Redux (IP-Adapter; recommended)
                  </label>
                  <label className="studio-toggle">
                    <input
                      type="checkbox"
                      checked={useCannyLock}
                      onChange={(e) => setUseCannyLock((e.currentTarget as HTMLInputElement).checked)}
                    />
                    Use Canny lock (preserve edges)
                  </label>
                  {useCannyLock && (
                    <>
                      <div className="studio-ref-row">
                        <label className="studio-ref-slider-label">Canny strength</label>
                        <input
                          type="range"
                          min={0} max={1} step={0.05}
                          value={cannyStrength}
                          onInput={(e) => setCannyStrength(Number((e.currentTarget as HTMLInputElement).value))}
                        />
                        <span className="studio-ref-slider-val">{cannyStrength.toFixed(2)}</span>
                      </div>
                      <div className="studio-ref-row">
                        <label className="studio-ref-slider-label">ControlNet</label>
                        <select
                          className="input"
                          value={cnModel}
                          onChange={(e) => setCnModel((e.currentTarget as HTMLSelectElement).value as ControlNetModel)}
                        >
                          {CONTROLNET_MODELS.map((m) => (
                            <option key={m.id} value={m.id} title={m.description}>{m.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
                <button
                  className="icon-btn studio-ref-clear"
                  onClick={() => setRefDataUrl(null)}
                  title="Remove reference"
                  type="button"
                >
                  <Icon name="x" size={12} />
                </button>
              </div>
            ) : (
              <div
                className="studio-ref-drop"
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <Icon name="image" size={20} />
                <div>
                  <div className="studio-ref-drop-headline">Add a reference image</div>
                  <div className="studio-ref-drop-sub">
                    Drop or click — PNG/JPEG/WebP, 16 MB cap
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={onPickFile}
                />
              </div>
            )}
          </ComposerRow>
        </div>
      )}
    </div>
  );
}

function ComposerRow({ label, children }: { label: string; children: any }) {
  return (
    <div className="studio-row">
      <label className="studio-row-label">{label}</label>
      {children}
    </div>
  );
}
